import { formatDate, formatTime } from "@memorang/helpers/date";
import { getReportSkills } from "@memorang/helpers/report";
import type { BaseItemSchema, MixedItemTag } from "@memorang/types";
import { type ReactNode, createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { getSkillIcon } from "../../../constants";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import AppbarAction from "../../AppbarAction";
import Box from "../../Box";
import Button from "../../Button";
import Container from "../../Container";
import { Skeleton } from "../../Skeleton";
import { SessionReportCardsSection } from "../SessionReportCardsSection";
import { SessionReportCardsSectionSkeleton } from "../SessionReportCardsSectionSkeleton";
import { SessionReportDisclaimer } from "../SessionReportDisclaimer";
import type { SessionReportItemTypeDetailSelection } from "../SessionReportItemTypeDetailDialog";
import type { SessionReportPremiumCardContent } from "../SessionReportPremiumCard";
import { SessionReportSkillCard } from "../SessionReportSkillCard";
import type { SessionReportProficiencyEstimateView } from "../Summary/SessionReportProficiencySection";
import { SessionReportSummary } from "../Summary/SessionReportSummary";
import { SessionReportSummarySkeleton } from "../Summary/SessionReportSummarySkeleton";

export type SessionReportAppletValue = {
	isLoading?: boolean;
	session: {
		sessionId: string;
		sessionDate: string;
		examName: string;
		examContextName: string;
		isSummativeTest: boolean;
		isDiagnostic: boolean;
	};
	report: {
		tags: MixedItemTag[];
		topics: string[];
		sessionItems: BaseItemSchema[];
		timeElapsed: number;
		numItems: number;
		overallScore?: {
			score: number;
			maxScore: number;
		};
		overallPoints?: {
			earned: number;
			total: number;
		};
		proficiencyEstimates?: SessionReportProficiencyEstimateView[];
	};
	cards: {
		showItemReview: boolean;
		premium?: SessionReportPremiumCardContent;
	};
	options: {
		onBack?: () => void;
		onContinue: () => void;
		onViewAllItemTypes: (filter?: string) => void;
		onNavigateToItems: () => void;
		onUpgrade?: () => void;
		onReview: (items: BaseItemSchema[]) => void;
		onOpenReviewAnswers: (items: BaseItemSchema[]) => void;
		onSelectQuestionType: (
			selection: SessionReportItemTypeDetailSelection,
		) => void;
		isReviewLoading?: boolean;
	};
};

type SessionReportAppletProps = {
	children: ReactNode;
	value: SessionReportAppletValue;
	nextActionSlot?: ReactNode;
	sessionTipSlot?: ReactNode;
	itemTypesTipSlot?: ReactNode;
};

const SessionReportAppletContext =
	createContext<SessionReportAppletValue | null>(null);

type SessionReportAppletSlots = {
	nextAction: ReactNode;
	sessionTip: ReactNode;
	itemTypesTip: ReactNode;
};

const SessionReportAppletSlotsContext = createContext<SessionReportAppletSlots>(
	{
		nextAction: null,
		sessionTip: null,
		itemTypesTip: null,
	},
);

const useSessionReportApplet = () => {
	const value = useContext(SessionReportAppletContext);
	if (!value) {
		throw new Error(
			"SessionReportApplet compound components must be rendered inside SessionReportApplet.",
		);
	}
	return value;
};

const SessionReportAppletProvider = ({
	children,
	value,
	nextActionSlot,
	sessionTipSlot,
	itemTypesTipSlot,
}: SessionReportAppletProps) => {
	const slots = useMemo(
		() => ({
			nextAction: nextActionSlot ?? null,
			sessionTip: sessionTipSlot ?? null,
			itemTypesTip: itemTypesTipSlot ?? null,
		}),
		[nextActionSlot, sessionTipSlot, itemTypesTipSlot],
	);

	return (
		<SessionReportAppletContext.Provider value={value}>
			<SessionReportAppletSlotsContext.Provider value={slots}>
				{children}
			</SessionReportAppletSlotsContext.Provider>
		</SessionReportAppletContext.Provider>
	);
};

const SessionReportAppletHeader = () => {
	const {
		session,
		isLoading = false,
		options: { onBack },
	} = useSessionReportApplet();
	const theme = useAppTheme();
	const { t } = useTranslation();

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			gap={4}
			backgroundColor={theme.colors.surface}
		>
			{onBack ? (
				<AppbarAction
					icon="arrow-left"
					accessibilityRole="button"
					accessibilityLabel="Go back"
					onPress={onBack}
				/>
			) : null}
			<Box
				flex={1}
				alignSelf="center"
				paddingLeft={onBack ? 0 : 24}
				paddingRight={24}
				paddingVertical={12}
				gap={4}
			>
				{isLoading ? (
					<>
						<Skeleton height={28} width="45%" />
						<Skeleton height={16} width="30%" />
					</>
				) : (
					<>
						<Text variant="titleMedium">
							{session.examName} &ndash; {session.sessionId}
						</Text>
						<Text>
							{formatDate(session.sessionDate)} &ndash;{" "}
							{t("report.startedAt", {
								time: formatTime(session.sessionDate),
							})}
						</Text>
					</>
				)}
			</Box>
		</Box>
	);
};

const SessionReportAppletSummary = () => {
	const { session, report, isLoading = false } = useSessionReportApplet();
	const { nextAction, sessionTip } = useContext(
		SessionReportAppletSlotsContext,
	);

	const showOverallScore =
		session.isSummativeTest &&
		!session.isDiagnostic &&
		Boolean(report.overallScore);

	if (isLoading) {
		return <SessionReportSummarySkeleton />;
	}

	return (
		<SessionReportSummary
			isSummativeTest={session.isSummativeTest}
			tags={report.tags}
			topics={report.topics}
			sessionItems={report.sessionItems}
			timeElapsed={report.timeElapsed}
			numItems={report.numItems}
			nextActionSlot={nextAction ?? undefined}
			sessionTipSlot={sessionTip ?? undefined}
			overallScore={showOverallScore ? report.overallScore : undefined}
			overallPoints={report.overallPoints}
			proficiencyEstimates={report.proficiencyEstimates}
		/>
	);
};

const SessionReportAppletCards = () => {
	const {
		session,
		report,
		cards,
		options,
		isLoading = false,
	} = useSessionReportApplet();
	const { itemTypesTip } = useContext(SessionReportAppletSlotsContext);

	if (isLoading) {
		return <SessionReportCardsSectionSkeleton />;
	}

	const skillTags = getReportSkills(report.tags, report.topics);
	const overallScore = report.overallScore;
	const showSkillBreakdown =
		session.isSummativeTest &&
		!session.isDiagnostic &&
		skillTags.length > 1 &&
		overallScore !== undefined;

	return (
		<Box gap={16} width="100%">
			{showSkillBreakdown ? (
				<SessionReportSkillCard
					numItems={report.numItems}
					maxScore={overallScore.maxScore}
					skills={skillTags.map((skill) => ({
						id: skill.id,
						label: skill.title,
						icon: getSkillIcon(skill.title),
						score: Math.round(skill.numPointsEarned ?? skill.score ?? 0),
					}))}
				/>
			) : null}
			<SessionReportCardsSection
				tags={report.tags}
				topics={report.topics}
				sessionItems={report.sessionItems}
				showItemReview={cards.showItemReview}
				premium={cards.premium}
				isSummativeTest={session.isSummativeTest}
				itemTypesTipSlot={itemTypesTip}
				onViewAllItemTypes={options.onViewAllItemTypes}
				onNavigateToItems={options.onNavigateToItems}
				onReview={options.onReview}
				onOpenReviewAnswers={options.onOpenReviewAnswers}
				onUpgrade={options.onUpgrade}
				onSelectQuestionType={options.onSelectQuestionType}
				isReviewLoading={options.isReviewLoading}
			/>
		</Box>
	);
};

const SessionReportAppletFooter = () => {
	const {
		options: { onBack, onContinue },
	} = useSessionReportApplet();
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { t } = useTranslation();
	const insets = useSafeAreaInsets();

	if (onBack) {
		return null;
	}

	return (
		<Box
			backgroundColor={theme.colors.surface}
			paddingTop={24}
			paddingBottom={24 + insets.bottom}
		>
			<Container autoSize>
				<Box
					flexDirection="row"
					justifyContent="flex-end"
					gap={8}
					paddingHorizontal={isMobile ? 24 : 32}
				>
					<Button mode="outlined" icon="home-outline" onPress={onContinue}>
						{t("report.actions.goToHome")}
					</Button>
				</Box>
			</Container>
		</Box>
	);
};

const SessionReportAppletDisclaimer = () => {
	const { session, isLoading = false } = useSessionReportApplet();

	if (isLoading || !session.isSummativeTest) {
		return null;
	}

	return <SessionReportDisclaimer examName={session.examContextName} />;
};

const SessionReportAppletBody = () => (
	<Box gap={16} width="100%">
		<SessionReportAppletSummary />
		<SessionReportAppletCards />
		<SessionReportAppletDisclaimer />
	</Box>
);

export const SessionReportApplet = Object.assign(SessionReportAppletProvider, {
	Header: SessionReportAppletHeader,
	Summary: SessionReportAppletSummary,
	Cards: SessionReportAppletCards,
	Footer: SessionReportAppletFooter,
	Body: SessionReportAppletBody,
});
